import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(null);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    // Check localStorage first
    const saved = localStorage.getItem('telcavoip_theme');
    if (saved) return saved;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches) {
      return 'light';
    }
    
    // Default to dark
    return 'dark';
  });

  const setTheme = (newTheme) => {
    setThemeState(newTheme);
    localStorage.setItem('telcavoip_theme', newTheme);
  };

  const toggleTheme = () => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  };

  useEffect(() => {
    const root = document.documentElement;
    
    if (theme === 'light') {
      // Light theme with better contrast
      root.style.setProperty('--background', '220 20% 97%');
      root.style.setProperty('--foreground', '222 47% 11%');
      root.style.setProperty('--card', '0 0% 100%');
      root.style.setProperty('--card-foreground', '222 47% 11%');
      root.style.setProperty('--popover', '0 0% 100%');
      root.style.setProperty('--popover-foreground', '222 47% 11%');
      root.style.setProperty('--primary', '239 84% 60%');
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--secondary', '220 14% 92%');
      root.style.setProperty('--secondary-foreground', '222 47% 11%');
      root.style.setProperty('--muted', '220 14% 92%');
      root.style.setProperty('--muted-foreground', '220 9% 40%');
      root.style.setProperty('--accent', '220 14% 92%');
      root.style.setProperty('--accent-foreground', '222 47% 11%');
      root.style.setProperty('--destructive', '350 89% 55%');
      root.style.setProperty('--destructive-foreground', '0 0% 100%');
      root.style.setProperty('--border', '220 13% 85%');
      root.style.setProperty('--input', '220 13% 85%');
      root.style.setProperty('--ring', '239 84% 60%');
      // Custom properties for email detail panel contrast
      root.style.setProperty('--email-list-bg', '220 20% 97%');
      root.style.setProperty('--email-detail-bg', '0 0% 100%');
      root.style.setProperty('--sidebar-bg', '0 0% 100%');
      root.classList.remove('dark');
      root.classList.add('light');
    } else {
      // Dark theme
      root.style.setProperty('--background', '222 47% 3%');
      root.style.setProperty('--foreground', '210 40% 98%');
      root.style.setProperty('--card', '217 33% 8%');
      root.style.setProperty('--card-foreground', '210 40% 96%');
      root.style.setProperty('--popover', '217 33% 8%');
      root.style.setProperty('--popover-foreground', '210 40% 98%');
      root.style.setProperty('--primary', '239 84% 67%');
      root.style.setProperty('--primary-foreground', '0 0% 100%');
      root.style.setProperty('--secondary', '217 33% 17%');
      root.style.setProperty('--secondary-foreground', '210 40% 98%');
      root.style.setProperty('--muted', '217 33% 17%');
      root.style.setProperty('--muted-foreground', '215 20% 65%');
      root.style.setProperty('--accent', '243 75% 50%');
      root.style.setProperty('--accent-foreground', '0 0% 100%');
      root.style.setProperty('--destructive', '350 89% 60%');
      root.style.setProperty('--destructive-foreground', '0 0% 100%');
      root.style.setProperty('--border', '217 33% 17%');
      root.style.setProperty('--input', '217 33% 17%');
      root.style.setProperty('--ring', '239 84% 67%');
      // Custom properties for email detail panel contrast
      root.style.setProperty('--email-list-bg', '222 47% 3%');
      root.style.setProperty('--email-detail-bg', '217 33% 8%');
      root.style.setProperty('--sidebar-bg', '217 33% 6%');
      root.classList.remove('light');
      root.classList.add('dark');
    }
  }, [theme]);

  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (e) => {
      // Only auto-switch if user hasn't manually set a preference
      if (!localStorage.getItem('telcavoip_theme')) {
        setThemeState(e.matches ? 'dark' : 'light');
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
